import { generateToDocFn } from '@dadohr/shared/domainTypes/document';
import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  SchedulesRecentlyExecutingDoc,
  ScheduleStatus
} from '@dadohr/shared/domainTypes/schedules';
import { FS } from '@dadohr/shared/versions';
import { Card, Grid } from '@material-ui/core';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { InlineLoader, Loader } from '../../../../../components/Loader';
import { SomethingWentWrong } from '../../../../../components/SomethingWentWrong';
import { usePromise } from '../../../../../hooks/usePromise';
import { CanvasBar } from '../../../../../layout/Canvas';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../layout/Flex';
import { Page } from '../../../../../layout/Page';
import { Section } from '../../../../../layout/Section';
import { getAllByIdAsMap, store } from '../../../../../services/firebase';
import {
  createDocumentListenerGetter,
  useDocumentListener
} from '../../../../../services/firecache/documentListener';
import { toScheduleDoc } from '../../../../../services/schedules/documents';
import { now } from '../../../../../services/time';
import { Json } from '../../../../components/Json';
import { TopNav } from '../../../../layout/TopNav';
import { useOrgById } from '../../../../services/orgs';
import { OrgNavHeader } from '../../components/OrgNavHeader';

const SUMMARY_COLL_NAME = 'schedulesRecentlyExecutingV1';
const toSummaryDoc = generateToDocFn<SchedulesRecentlyExecutingDoc>();

const summaryDocStore = createDocumentListenerGetter(
  (orgId) => store().collection(SUMMARY_COLL_NAME).doc(orgId),
  toSummaryDoc,
  (orgId) => ({
    orgId,
    pendingIds: [],
    retryIdsAndTheirAttemptCount: [],
    lastUpdate: now()
  })
);

const HangingSchedules = ({ ids }: { ids: string[] }) => {
  const [data] = usePromise(() => {
    return getAllByIdAsMap(
      store().collection(FS.schedules),
      ids,
      toScheduleDoc
    );
  }, [ids]);

  return (
    <>
      <CanvasBar>Hanging Schedules</CanvasBar>
      <Card>
        <FlexContainerVertical fullWidth spacing={2}>
          {ids.map((id) => {
            if (!data) {
              return (
                <FlexContainer key={id} fullWidth>
                  <div>{id}</div>
                  <InlineLoader />
                </FlexContainer>
              );
            }
            const d = data[id];
            if (!d) {
              return (
                <FlexContainer key={id} fullWidth>
                  <div>{id}</div>
                  <div>NOT FOUND</div>
                </FlexContainer>
              );
            }

            if (
              d.data.status === ScheduleStatus.PENDING ||
              d.data.status === ScheduleStatus.RETRY
            ) {
              return <Json key={id} data={d} />;
            }
            // nothing to show here - this one actually went through
            return null;
          })}
        </FlexContainerVertical>
      </Card>
    </>
  );
};

export const PageOrgSchedulesHanging = ({ orgId }: { orgId: string }) => {
  const [org, loadingOrg] = useOrgById(orgId);
  const [summary, loadingSummary] = useDocumentListener(summaryDocStore(orgId));
  const ids = useMemo(() => {
    if (!summary) return EMPTY_ARR;
    // Legacy BE wrote retryIdsAndTheirAttemptCount as Array<[id, attempts]>;
    // Gen2 writes it as { [id]: attempts } because Firestore rejects nested
    // arrays (see functions/schedules/src/service.ts:303). Handle both
    // shapes here so the page works mid-migration regardless of which
    // version of the schedules service produced the doc.
    const retry = summary.data.retryIdsAndTheirAttemptCount;
    const retryIds = Array.isArray(retry)
      ? retry.map((x) => x[0])
      : retry
      ? Object.keys(retry)
      : [];
    return [...summary.data.pendingIds, ...retryIds];
  }, [summary]);

  if (loadingOrg) {
    return <Loader height={500} />;
  }

  if (!org) {
    return <SomethingWentWrong height={500} />;
  }

  return (
    <>
      <TopNav>
        <Grid container alignItems="center" justify="space-between">
          <Grid item>
            <OrgNavHeader
              parts={[
                <Link to={`/orgs/${org.id}`}>{org.data.name}</Link>,
                'Schedules Hanging'
              ]}
            />
          </Grid>
        </Grid>
      </TopNav>
      <Page style={{ marginTop: 50 }}>
        {loadingSummary && <Loader height={300} />}
        {summary && (
          <>
            <Section>
              <Card>Schedules tried to execute in last run: {ids.length}</Card>
            </Section>
            {ids.length && (
              <Section>
                <HangingSchedules ids={ids} />
              </Section>
            )}
          </>
        )}
      </Page>
    </>
  );
};
